/**
 * In-app authenticated diagnostic panel.
 *
 * Why it exists
 *   Direct browser navigation to /api/platform/diagnostics returned
 *   "Authenticated user required" in production for a logged-in user.
 *   The most likely causes are:
 *     - the API origin is different from the app origin (cookie not
 *       sent on direct-URL navigation across origins),
 *     - the cookie has SameSite=Lax + Secure and the URL was opened
 *       in a context where the cookie isn't included,
 *     - the auth context is stored in memory by the React app and
 *       direct URL nav doesn't re-bootstrap it.
 *
 *   This panel sidesteps all of that. It calls the API via apiRequest
 *   (which uses `credentials: 'include'` — the same mechanism every
 *   other authenticated call uses) and renders the response inline.
 *
 *   Calls TWO endpoints:
 *     1. /api/auth/session-diagnostics — works for ANY logged-in user;
 *        surfaces the auth payload as the server sees it.
 *     2. /api/platform/diagnostics — works only for platform admins;
 *        confirms whether the platform gate ALSO recognizes them.
 *
 *   Both responses are rendered as raw JSON in a <pre> block that can
 *   be copy/pasted into a support ticket.
 *
 * Activation
 *   Append `?diag=1` to any URL inside the app. The panel mounts as
 *   a fixed overlay over the current page so it works regardless of
 *   which shell (platform / client / director) the operator is in.
 */

import React, { useEffect, useState } from 'react';
import { ApiError, apiRequest } from '../services/apiClient';

function formatJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function StatusLine({ label, value, tone = 'neutral' }) {
  return (
    <div
      className={`session-diagnostic-status session-diagnostic-status--${tone}`}
      data-testid={`session-diagnostic-status-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <strong>{label}:</strong> {value}
    </div>
  );
}

export default function SessionDiagnosticPanel({ onClose = null }) {
  const [sessionData, setSessionData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [platformError, setPlatformError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      apiRequest('/api/auth/session-diagnostics'),
      apiRequest('/api/platform/diagnostics')
    ]).then((results) => {
      if (cancelled) return;
      const [session, platform] = results;
      if (session.status === 'fulfilled') {
        setSessionData(session.value);
      } else {
        const err = session.reason;
        setSessionError({
          status: err instanceof ApiError ? err.status : 0,
          message: err?.message || String(err),
          payload: err instanceof ApiError ? err.payload : null
        });
      }
      if (platform.status === 'fulfilled') {
        setPlatformData(platform.value);
      } else {
        const err = platform.reason;
        setPlatformError({
          status: err instanceof ApiError ? err.status : 0,
          message: err?.message || String(err),
          payload: err instanceof ApiError ? err.payload : null
        });
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const sessionOk = Boolean(sessionData);
  const sessionStatus = sessionOk
    ? 'authenticated'
    : sessionError?.status === 401
      ? 'NOT authenticated — cookie missing or expired'
      : `error (${sessionError?.status || 0})`;

  const platformOk = Boolean(platformData);
  const platformStatus = platformOk
    ? 'platform admin'
    : platformError?.status === 403
      ? 'NOT recognized as platform admin (gate returned 403)'
      : platformError?.status === 401
        ? 'NOT authenticated for the platform endpoint'
        : `error (${platformError?.status || 0})`;

  return (
    <div
      role="dialog"
      aria-label="Session diagnostic panel"
      data-testid="session-diagnostic-panel"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        bottom: 20,
        width: 'min(720px, 90vw)',
        background: 'white',
        border: '2px solid #1f2937',
        borderRadius: 8,
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif'
      }}
    >
      <header
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f3f4f6'
        }}
      >
        <strong>Session diagnostic</strong>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            data-testid="session-diagnostic-close"
            style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
          >
            Close
          </button>
        ) : null}
      </header>

      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>
        <StatusLine
          label="Session"
          value={loading ? '…' : sessionStatus}
          tone={sessionOk ? 'success' : 'error'}
        />
        <StatusLine
          label="Platform"
          value={loading ? '…' : platformStatus}
          tone={platformOk ? 'success' : sessionOk ? 'warning' : 'error'}
        />
        {sessionData?.runtime?.commit ? (
          <StatusLine
            label="Deployed commit"
            value={`${sessionData.runtime.commit.slice(0, 12)} (${sessionData.runtime.environment || 'unknown'})`}
            tone="neutral"
          />
        ) : null}
      </div>

      <div style={{ overflow: 'auto', padding: '12px 16px', flex: 1, fontSize: 12 }}>
        <section data-testid="session-diagnostic-session-block">
          <h3 style={{ fontSize: 13, margin: '0 0 6px' }}>GET /api/auth/session-diagnostics</h3>
          <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 4, overflow: 'auto', margin: 0 }}>
            {loading
              ? 'loading…'
              : sessionData
                ? formatJson(sessionData)
                : formatJson(sessionError)}
          </pre>
        </section>

        <section style={{ marginTop: 16 }} data-testid="session-diagnostic-platform-block">
          <h3 style={{ fontSize: 13, margin: '0 0 6px' }}>GET /api/platform/diagnostics</h3>
          <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 4, overflow: 'auto', margin: 0 }}>
            {loading
              ? 'loading…'
              : platformData
                ? formatJson(platformData)
                : formatJson(platformError)}
          </pre>
        </section>
      </div>
    </div>
  );
}
